// ============================================
// Digital Glitch Post-Process Effect
// ============================================
// Time-quantized signal corruption: scanline tears, block corruption with
// channel-roll, and spiking RGB split. Great for damage feedback, hacking
// screens, teleports, or a permanently haunted TV.

import type { IRenderContext } from '../../core/IRenderContext';
import {
  PostProcessEffect,
  type PostProcessContext,
  type PostProcessParameter,
} from '../PostProcessStack';
import { GLITCH_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Glitch post-processing effect
 */
export class GlitchEffect extends PostProcessEffect {
  readonly type = 'glitch';
  readonly displayName = 'Glitch';

  // Parameters
  lineGlitch = 0.5;
  blockGlitch = 0.5;
  rgbSplit = 0.5;
  speed = 1.0;
  blockSize = 0.15;

  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = GLITCH_LAYOUT.createWriter();

  getParameters(): PostProcessParameter[] {
    return [
      { name: 'lineGlitch', type: 'float', min: 0, max: 1, default: 0.5, description: 'Scanline tearing' },
      { name: 'blockGlitch', type: 'float', min: 0, max: 1, default: 0.5, description: 'Block corruption' },
      { name: 'rgbSplit', type: 'float', min: 0, max: 1, default: 0.5, description: 'RGB split' },
      { name: 'speed', type: 'float', min: 0.1, max: 5, default: 1.0, description: 'Glitch tempo' },
      { name: 'blockSize', type: 'float', min: 0.02, max: 0.5, default: 0.15, description: 'Corruption block size' },
    ];
  }

  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'lineGlitch': return this.lineGlitch;
      case 'blockGlitch': return this.blockGlitch;
      case 'rgbSplit': return this.rgbSplit;
      case 'speed': return this.speed;
      case 'blockSize': return this.blockSize;
      default: return 0;
    }
  }

  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'lineGlitch': this.lineGlitch = Math.max(0, Math.min(1, value)); break;
      case 'blockGlitch': this.blockGlitch = Math.max(0, Math.min(1, value)); break;
      case 'rgbSplit': this.rgbSplit = Math.max(0, Math.min(1, value)); break;
      case 'speed': this.speed = Math.max(0.1, Math.min(5, value)); break;
      case 'blockSize': this.blockSize = Math.max(0.02, Math.min(0.5, value)); break;
    }
  }

  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();

    const glitchWgsl = getCompiledShader('postprocess/glitch');
    this.shader = ctx.createShader(glitchWgsl, glitchWgsl);

    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'glitch-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'glitch-ldr',
    });
  }

  protected onRender(context: PostProcessContext): void {
    const { ctx, source, time, intensity, isHdrTarget } = context;

    // NOTE: PostProcessStack already set render target and viewport

    this.uniformWriter.setAll({
      lineGlitch: this.lineGlitch * intensity,
      blockGlitch: this.blockGlitch * intensity,
      rgbSplit: this.rgbSplit * intensity,
      speed: this.speed,
      blockSize: this.blockSize,
      // Wrapped so the hash seed keeps float precision on long sessions.
      time: time % 64.0,
      _pad: [0, 0],
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }

  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
